import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrency } from '@/contexts/CurrencyContext';

interface ConvertedNetWorth {
  net_worth: number;
  total_assets: number;
  total_liabilities: number;
  currency: string;
  rates_updated_at: string | null;
}

export function useConvertedNetWorth() {
  const { user } = useAuth();
  const { currency } = useCurrency();
  const [data, setData] = useState<ConvertedNetWorth | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConvertedNetWorth = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const { data: result, error: rpcError } = await supabase.rpc('get_converted_net_worth', {
        target_curr: currency,
      });

      if (rpcError) {
        throw new Error(rpcError.message);
      }

      setData(result as ConvertedNetWorth);
    } catch (err) {
      console.error('Error fetching converted net worth:', err);
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [user, currency]);

  useEffect(() => {
    fetchConvertedNetWorth();
  }, [fetchConvertedNetWorth]);

  // Listen for account changes to refresh
  useEffect(() => {
    const handler = () => fetchConvertedNetWorth();
    window.addEventListener('accounts-updated', handler);
    return () => window.removeEventListener('accounts-updated', handler);
  }, [fetchConvertedNetWorth]);

  return { data, loading, error, refetch: fetchConvertedNetWorth };
}
